import React, { createContext, useContext } from 'react';

import { Box, Tooltip } from '@mui/material';

import calibration from './confidenceCalibration.json';
import { emptyPredictionTooltip } from './emptyPredictionText';
import { ConfidenceTier } from './types';

export const StalePredictionContext = createContext<string | null>(null);

export const confidenceLabels: Record<ConfidenceTier, string> = {
  high: '●●● High',
  medium: '●●○ Medium',
  low: '●○○ Low',
  indicative: '○○○ Indicative',
};

export default function ConfidenceBadge({
  tier = 'indicative',
  capacity,
  source,
  score,
  aggregate,
  mix,
}: {
  tier?: ConfidenceTier;
  capacity?: number | null;
  source?: string | null;
  score?: number | null;
  aggregate?: boolean;
  mix?: string;
}) {
  const staleTooltip = useContext(StalePredictionContext);
  if (staleTooltip || source === 'zero_model') {
    tier = 'indicative';
  }
  const rate = tier === 'indicative' ? null : calibration.actualRates[tier];
  const tolerance = Math.max(2, (capacity || 0) * 0.1);
  const title =
    staleTooltip ||
    mix ||
    (aggregate
      ? 'Most predicted travellers come from departures in this tier. This is the traveller-weighted most common tier, not a confidence score for the total.'
      : source === 'zero_model'
        ? emptyPredictionTooltip
        : tier === 'indicative'
          ? 'Early estimate from tour history and booking signals. Good for spotting slow sellers, not for exact numbers.'
          : source === 'pace'
            ? `Pace estimate: ${Math.round(score || 0)}% of similar held-out predictions finished within ±${tolerance.toLocaleString('en-AU')} travellers. Pace confidence is capped at Medium.`
            : `${tier.charAt(0).toUpperCase()}${tier.slice(1)} confidence: about ${Math.round((rate || 0) * 10)} in 10 predictions like this finish within ±${tolerance.toLocaleString('en-AU')} travellers. Measured on ${calibration.testQuarter}.`);
  return (
    <Tooltip title={title}>
      <Box
        component="span"
        data-confidence-pill
        tabIndex={0}
        sx={{
          display: 'inline-block',
          borderRadius: 8,
          bgcolor:
            tier === 'high'
              ? '#17636a'
              : tier === 'medium'
                ? 'rgba(66,125,182,.2)'
                : tier === 'low'
                  ? 'rgba(110,140,165,.12)'
                  : 'transparent',
          border: tier === 'indicative' ? '1px dashed #898781' : '1px solid transparent',
          px: 0.75,
          py: 0.4,
          fontSize: 13,
          fontWeight: 600,
          color: tier === 'high' ? '#fff' : tier === 'medium' ? 'var(--tcc-predicted-text, #326799)' : 'text.secondary',
          whiteSpace: 'nowrap',
          fontStyle: tier === 'indicative' ? 'italic' : 'normal',
        }}
      >
        {confidenceLabels[tier]}
      </Box>
    </Tooltip>
  );
}
